import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Pin } from 'lucide-react';

const TAG_COLORS = {
  UPDATE: '#00C8FF',
  EVENT: '#FFB52E',
  MILESTONE: '#7CFF4D',
  STORY: '#FF7A18'
};

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

export default function NewsPage() {
  const [posts, setPosts] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = 'TOONVERSE NEWS — ARC TOONS';
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/news`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('Failed to load'))))
      .then((d) => setPosts(Array.isArray(d) ? d : []))
      .catch(() => setError('The hologram feed glitched. Try refreshing.'));
  }, []);

  return (
    <div className="relative min-h-screen pb-28 pt-32 sm:pt-36" data-testid="news-page">
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{ background: 'radial-gradient(55% 40% at 50% 0%, rgba(22,119,255,0.14) 0%, transparent 70%)' }}
      />
      <div className="relative mx-auto max-w-[900px] px-6 sm:px-10">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-electric">07 — NEWS BOARD</p>
          <h1 className="mt-4 font-display text-3xl font-extrabold uppercase tracking-tight sm:text-4xl lg:text-5xl">
            TOONVERSE{' '}
            <span className="bg-gradient-to-r from-arc via-electric to-gold bg-clip-text text-transparent">
              NEWS
            </span>
          </h1>
          <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.3em] text-snow/50" data-testid="news-subtitle">
            TRANSMISSIONS FROM TOON CITY
          </p>
        </motion.div>

        {error && (
          <p className="mt-10 rounded-2xl border border-ember/40 bg-ember/10 p-4 text-sm text-ember" data-testid="news-error">
            {error}
          </p>
        )}

        {posts === null && !error && (
          <div className="mt-12 flex flex-col gap-5" data-testid="news-loading">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-40 animate-pulse rounded-3xl border border-electric/10 bg-navy2/50" />
            ))}
          </div>
        )}

        {posts !== null && posts.length === 0 && !error && (
          <div
            className="mt-12 rounded-3xl border border-dashed border-electric/25 p-12 text-center"
            data-testid="news-empty"
          >
            <p className="font-display text-lg font-bold uppercase tracking-tight text-snow/70">
              NO TRANSMISSIONS YET
            </p>
            <p className="mt-3 text-sm text-snow/50">
              The Toons are still writing their first story. Check back soon.
            </p>
          </div>
        )}

        <div className="mt-12 flex flex-col gap-5" data-testid="news-feed">
          {(posts || []).map((post, i) => (
            <motion.article
              key={post.id}
              data-testid={`news-post-${i}`}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
              className={`rounded-3xl border bg-navy2/70 p-6 sm:p-8 ${
                post.pinned ? 'border-gold/40 shadow-[0_0_36px_rgba(255,181,46,0.15)]' : 'border-electric/15'
              }`}
              style={{ backdropFilter: 'blur(10px)' }}
            >
              <div className="flex flex-wrap items-center gap-3">
                <span
                  className="rounded-full border px-3 py-1 font-mono text-[9px] uppercase tracking-[0.3em]"
                  style={{
                    borderColor: `${TAG_COLORS[post.tag] || '#00C8FF'}66`,
                    color: TAG_COLORS[post.tag] || '#00C8FF'
                  }}
                  data-testid={`news-post-tag-${i}`}
                >
                  {post.tag}
                </span>
                {post.pinned && (
                  <span className="flex items-center gap-1.5 rounded-full border border-dashed border-gold/60 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.3em] text-gold" data-testid={`news-post-pinned-${i}`}>
                    <Pin className="h-3 w-3" /> PINNED
                  </span>
                )}
                <time className="ml-auto font-mono text-[10px] uppercase tracking-[0.2em] text-snow/40" dateTime={post.created_at}>
                  {fmtDate(post.created_at)}
                </time>
              </div>
              <h2 className="mt-4 font-display text-xl font-extrabold uppercase tracking-tight text-snow sm:text-2xl" data-testid={`news-post-title-${i}`}>
                {post.title}
              </h2>
              <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-snow/70" data-testid={`news-post-body-${i}`}>
                {post.body}
              </p>
            </motion.article>
          ))}
        </div>
      </div>
    </div>
  );
}
